import { useMemo } from "react";
import { useConfig } from "../contexts/ConfigContext.jsx";

export default function OrbitingTagCloud({
  page = "default",
  intensity = "hero",
  dim = false
}) {
  const { config } = useConfig();

  const tags =
    config.orbitingTags?.[page] ||
    config.orbitingTags?.default ||
    [];

  const baseOpacity = intensity === "chat" ? "opacity-70" : "opacity-70";
  const scale = intensity === "chat" ? "scale-90" : "scale-100";

  const nodes = useMemo(() => {
    if (intensity === "chat") {
      const chatPositions = [
        { left: '3%', top: '12%' },
        { left: '31%', top: '3%' },
        { left: '47%', top: '8%' },
        { left: '79%', top: '5%' },
        { left: '88%', top: '18%' },
        { left: '12%', top: '31%' },
        { left: '28%', top: '38%' },
        { left: '61%', top: '27%' },
        { left: '73%', top: '42%' },
        { left: '91%', top: '33%' },
        { left: '7%', top: '58%' },
        { left: '39%', top: '63%' },
        { left: '54%', top: '55%' },
        { left: '77%', top: '68%' },
        { left: '93%', top: '59%' }
      ];

      return tags.map((tag, i) => ({
        ...tag,
        floatSpeed: 15 + (i * 1.8) + (i % 3) * 2,
        delay: i * 0.7 + (i % 4) * 0.3,
        blur:
          i % 5 === 0
            ? "blur-[0.8px]"
            : i % 3 === 0
            ? "blur-[0.4px]"
            : "blur-[0.6px]",
        size: i % 7 === 0 ? "text-[11px]" : "text-[10px]",
        ...chatPositions[i % chatPositions.length]
      }));
    }

    // HERO MODE
    const positions = [
      { left: '6%', top: '15%' },
      { left: '23%', top: '6%' },
      { left: '44%', top: '9%' },
      { left: '69%', top: '12%' },
      { left: '87%', top: '21%' },
      { left: '8%', top: '38%' },
      { left: '18%', top: '47%' },
      { left: '52%', top: '35%' },
      { left: '74%', top: '49%' },
      { left: '89%', top: '56%' },
      { left: '14%', top: '71%' },
      { left: '31%', top: '78%' },
      { left: '46%', top: '68%' },
      { left: '65%', top: '82%' },
      { left: '82%', top: '76%' }
    ];

    return tags.map((tag, i) => ({
      ...tag,
      floatSpeed: 10 + (i * 1.4) + (i % 4) * 3,
      delay: i * 0.6 + (i % 3) * 0.4,
      blur:
        i % 5 === 0
          ? "blur-[0.8px]"
          : i % 3 === 0
          ? "blur-[0.4px]"
          : i % 2 === 0
          ? "blur-[0.5px]"
          : "blur-[0.7px]",
      size:
        i % 5 === 0
          ? "text-[16px]"
          : i % 7 === 0
          ? "text-[12px]"
          : "text-[14px]",
      ...positions[i % positions.length]
    }));
  }, [intensity, tags]);

  return (
    <div
      className={`absolute inset-0 pointer-events-none ${baseOpacity} ${scale}`}
    >
      {nodes.map((tag, i) => (
        <span
          key={i}
          className={`
            absolute
            ${tag.size}
            ${tag.blur}
            font-mono tracking-widest
            bg-clip-text text-transparent bg-gradient-to-r ${tag.gradient}
            ${tag.pulse && !dim ? "animate-pulseGlow" : ""}
          `}
          style={{
            left: tag.left,
            top: tag.top,
            animation: `orbit-${i % 3} ${tag.floatSpeed}s ease-in-out infinite`,
            animationDelay: `${tag.delay}s`,
            filter: "drop-shadow(0 0 8px rgba(255,255,255,0.25))",
            transform: `rotate(${(i * 7) % 15 - 7}deg)`
          }}
        >
          {tag.label}
        </span>
      ))}
    </div>
  );
}
